import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Dashboard,
  Lock,
  Speed,
  ErrorOutline,
  Refresh,
  Inbox,
  GetApp,
  InfoOutlined,
  Settings,
  Fullscreen,
  FullscreenExit,
  ShowChart,
  BarChart as BarChartIcon,
} from "@material-ui/icons";
import Tooltip from "@material-ui/core/Tooltip";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";

// ─── Constants ────────────────────────────────────────────────
const PRIMARY = "#667eea";
const DANGER = "#e74c3c";
const SUCCESS = "#27ae60";
const WARNING = "#f39c12";

const DEFAULT_THRESHOLDS = {
  errorRateWarning:    5,   // %
  errorRateCritical:   15,  // %
  retryRateWarning:    3,   // %
  retryRateCritical:   10,  // %
  throughputWarning:   80,  // % — abaixo = warning
  throughputCritical:  95,  // % — acima = ok
  lockRateWarning:     20,  // %
  lockRateCritical:    50,  // %
  parallelChgWarning:  10,  // ajustes
  parallelChgCritical: 50,  // ajustes
};

const mergeThresholds = (custom) => ({ ...DEFAULT_THRESHOLDS, ...custom });

const GROUP_DETAIL_TABS = [
  { id: "overview",    label: "Visão Geral",  icon: <Dashboard style={{ fontSize: 18 }} /> },
  { id: "queue",       label: "Fila",         icon: <Inbox style={{ fontSize: 18 }} /> },
  { id: "locks",       label: "Locks",        icon: <Lock style={{ fontSize: 18 }} /> },
  { id: "performance", label: "Performance",  icon: <Speed style={{ fontSize: 18 }} /> },
  { id: "errors",      label: "Erros",        icon: <ErrorOutline style={{ fontSize: 18 }} /> },
];

const pad = (n) => String(n).padStart(2, "0");
const toLocalDT = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toISO = (s) => new Date(s).toISOString();

// Default: dia atual
const _today = new Date();
const DEFAULT_FROM = toLocalDT(new Date(_today.getFullYear(), _today.getMonth(), _today.getDate(), 0, 0));
const DEFAULT_TO   = toLocalDT(new Date(_today.getFullYear(), _today.getMonth(), _today.getDate(), 23, 59));

// Converte YYYY-MM-DD para from/to do dia completo
const dayToRange = (dateStr) => ({
  from: toLocalDT(new Date(dateStr + "T00:00:00")),
  to:   toLocalDT(new Date(dateStr + "T23:59:59")),
});

const formatDuration = (sec) => {
  if (sec == null) return "—";
  if (sec >= 86400) return `${Math.round(sec / 86400)} dias`;
  if (sec >= 3600)  return `${Math.round(sec / 3600)} horas`;
  if (sec >= 60)    return `${Math.round(sec / 60)} minutos`;
  return `${sec}s`;
};
const formatBytes = (bytes) => {
  if (bytes == null) return "—";
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024)    return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
};

// ─── CSV export utility ───────────────────────────────────────
function exportToCSV(rows, headers, filename) {
  const bom = "\uFEFF";
  const sep = ";";
  const headerLine = headers.map((h) => `"${h.label}"`).join(sep);
  const dataLines = rows.map((row) =>
    headers.map((h) => {
      const v = row[h.key] ?? "";
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(sep)
  );
  const csv = bom + [headerLine, ...dataLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Shared styled components ─────────────────────────────────
const ExportBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 14px;
  border: 1.5px solid #27ae60;
  border-radius: 8px;
  background: white;
  color: #27ae60;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #f0faf4; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const REFRESH_OPTIONS = [
  { value: 0,      label: "Desativado" },
  { value: 15000,  label: "15s" },
  { value: 30000,  label: "30s" },
  { value: 60000,  label: "1 min" },
  { value: 120000, label: "2 min" },
  { value: 300000, label: "5 min" },
];

// ─── Shared filter bar ────────────────────────────────────────
function FilterBar({ from, onFrom, onTo, onRefresh, loading, children }) {
  // extrai só a data (YYYY-MM-DD) do valor datetime-local
  const dateValue = from ? from.slice(0, 10) : "";

  const handleDateChange = (e) => {
    if (!e.target.value) return;
    const r = dayToRange(e.target.value);
    onFrom(r.from);
    onTo(r.to);
  };

  return (
    <FilterRow>
      <FilterGroup>
        <FilterLabel>Data:</FilterLabel>
        <DateInput type="date" value={dateValue} onChange={handleDateChange} />
      </FilterGroup>
      {children}
      <RefreshBtn onClick={onRefresh} disabled={loading}>
        {loading ? <BtnSpinner /> : <Refresh style={{ fontSize: 18, marginRight: 4 }} />}
        {loading ? "Atualizando..." : "Atualizar"}
      </RefreshBtn>
    </FilterRow>
  );
}

// ─── QUEUE TAB ───────────────────────────────────────────────
function QueueTab({ grupoId, refreshInterval }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const { data } = await fetchClient.get(`/worker/${grupoId}/queue`);
      setInfo(data);
    } catch {
      message.error("Erro ao carregar informações da fila");
    } finally {
      setLoading(false);
    }
  }, [grupoId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setInfo(null); fetchData(); }, [grupoId]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [fetchData, refreshInterval]);

  if (loading && !info) return <Loader />;

  const msgs = info?.messages || {};
  const cfg  = info?.config   || {};
  const meta = info?.metadata || {};

  return (
    <TabContent>
      <FilterRow>
        <span style={{ fontFamily: "Mulish, sans-serif", fontSize: 13, color: "#64748b" }}>
          Snapshot em tempo real da fila SQS · atualização automática a cada 30s
        </span>
        <RefreshBtn onClick={fetchData} disabled={loading}>
          {loading ? <BtnSpinner /> : <Refresh style={{ fontSize: 18, marginRight: 4 }} />}
          {loading ? "Atualizando..." : "Atualizar"}
        </RefreshBtn>
      </FilterRow>

      <SectionCard>
        <SectionTitle>Mensagens na Fila</SectionTitle>
        <QueueKpiGrid>
          <QueueKpiCard color={SUCCESS}>
            <QueueKpiValue color={SUCCESS}>{msgs.available ?? "—"}</QueueKpiValue>
            <QueueKpiLabel>Disponíveis</QueueKpiLabel>
            <QueueKpiSub>aguardando processamento</QueueKpiSub>
          </QueueKpiCard>
          <QueueKpiCard color="#e67e22">
            <QueueKpiValue color="#e67e22">{msgs.inFlight ?? "—"}</QueueKpiValue>
            <QueueKpiLabel>Em Vôo</QueueKpiLabel>
            <QueueKpiSub>visibility timeout ativo</QueueKpiSub>
          </QueueKpiCard>
          <QueueKpiCard color="#7f8c8d">
            <QueueKpiValue color="#7f8c8d">{msgs.delayed ?? "—"}</QueueKpiValue>
            <QueueKpiLabel>Atrasadas</QueueKpiLabel>
            <QueueKpiSub>com delivery delay</QueueKpiSub>
          </QueueKpiCard>
          <QueueKpiCard color={PRIMARY}>
            <QueueKpiValue color={PRIMARY}>{msgs.total ?? "—"}</QueueKpiValue>
            <QueueKpiLabel>Total</QueueKpiLabel>
            <QueueKpiSub>soma de todas as categorias</QueueKpiSub>
          </QueueKpiCard>
        </QueueKpiGrid>
      </SectionCard>

      <QueueTwoCol>
        <SectionCard>
          <SectionTitle>Configuração</SectionTitle>
          <QueueInfoList>
            <QueueInfoRow>
              <QueueInfoKey>Visibility Timeout</QueueInfoKey>
              <QueueInfoVal>{formatDuration(cfg.visibilityTimeoutSec)}</QueueInfoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Retenção de Mensagem</QueueInfoKey>
              <QueueInfoVal>{formatDuration(cfg.retentionPeriodSec)}</QueueInfoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Tamanho Máximo</QueueInfoKey>
              <QueueInfoVal>{formatBytes(cfg.maxMessageSizeBytes)}</QueueInfoVal>
            </QueueInfoRow>
          </QueueInfoList>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Metadados</SectionTitle>
          <QueueInfoList>
            <QueueInfoRow>
              <QueueInfoKey>Stage</QueueInfoKey>
              <QueueInfoVal><Tag>{info?.stage || "—"}</Tag></QueueInfoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Criada em</QueueInfoKey>
              <QueueInfoVal>{meta.createdAt ? new Date(meta.createdAt).toLocaleString("pt-BR") : "—"}</QueueInfoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Modificada em</QueueInfoKey>
              <QueueInfoVal>{meta.lastModifiedAt ? new Date(meta.lastModifiedAt).toLocaleString("pt-BR") : "—"}</QueueInfoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Queue ARN</QueueInfoKey>
              <QueueMonoVal title={meta.queueArn}>{meta.queueArn || "—"}</QueueMonoVal>
            </QueueInfoRow>
            <QueueInfoRow>
              <QueueInfoKey>Queue URL</QueueInfoKey>
              <QueueMonoVal title={info?.queueUrl}>{info?.queueUrl || "—"}</QueueMonoVal>
            </QueueInfoRow>
          </QueueInfoList>
        </SectionCard>
      </QueueTwoCol>
    </TabContent>
  );
}

// ─── HEALTH PANEL ─────────────────────────────────────────────
function HealthInfoTip({ text }) {
  return (
    <Tooltip title={text} arrow placement="top" enterDelay={150}>
      <HealthInfoIcon />
    </Tooltip>
  );
}

function HealthPanel({ summary, counters = null, locks = [], thresholds: rawThresholds }) {
  const thresholds = mergeThresholds(rawThresholds);
  const indicators = useMemo(() => {
    if (!summary.length) return null;

    const t = thresholds;
    const errW  = t.errorRateWarning   / 100;
    const errC  = t.errorRateCritical  / 100;
    const retW  = t.retryRateWarning   / 100;
    const retC  = t.retryRateCritical  / 100;
    const thrOk = t.throughputCritical / 100;
    const thrW  = t.throughputWarning  / 100;
    const lckW  = t.lockRateWarning    / 100;
    const lckC  = t.lockRateCritical   / 100;
    const pchW  = t.parallelChgWarning;
    const pchC  = t.parallelChgCritical;

    const totalProcessed = counters?.messages?.processed ?? summary.reduce((a, s) => a + (s.messagesProcessed || 0), 0);
    const totalErrors    = counters?.messages?.errors    ?? summary.reduce((a, s) => a + (s.messagesError     || 0), 0);
    const totalReceived  = counters?.messages?.received  ?? summary.reduce((a, s) => a + (s.messagesReceived  || 0), 0);
    // Cruza timestamps dos LOCK_DETECTED com os ciclos do summary (janela ±2 min)
    const lockTimes = locks
      .filter(l => l.eventType === "LOCK_DETECTED")
      .map(l => new Date(l.timestamp).getTime());
    const totalCiclos   = counters?.cycles?.total ?? summary.length;
    const ciclosComLock = lockTimes.length === 0
      ? summary.filter(s => (s.lockDurationSeconds || 0) > 0).length
      : summary.filter(s => {
          const st = new Date(s.timestamp).getTime();
          return lockTimes.some(lt => Math.abs(lt - st) < 2 * 60 * 1000);
        }).length;
    const totalRetries  = counters?.retries?.total ?? 0;
    const maxRetryCount = 0;

    // Usa parallelismChanges do counters se disponível, senão calcula via summary
    const sorted = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const parallelVals = sorted.map(s => s.parallelism).filter(v => v != null);
    let totalParallelChg = counters?.cycles?.parallelismChanges ?? (() => {
      let chg = 0;
      for (let i = 1; i < parallelVals.length; i++) {
        if (parallelVals[i] !== parallelVals[i - 1]) chg++;
      }
      return chg;
    })();
    const minParallel = parallelVals.length ? Math.min(...parallelVals) : null;
    const maxParallel = parallelVals.length ? Math.max(...parallelVals) : null;

    // Usa totalReceived como base — totalProcessed conta só sucessos, causaria taxa > 100%
    // Fallback para processed+failed caso received não esteja disponível no período
    const totalFailed  = totalErrors + totalRetries;
    const base         = Math.max(totalReceived, totalProcessed + totalFailed) || 1;
    const errorRate    = totalFailed  / base;
    const retryRate    = totalRetries / base;
    const throughput   = totalReceived > 0 ? totalProcessed / totalReceived : 1;
    const lockRate     = totalCiclos   > 0 ? ciclosComLock  / totalCiclos   : 0;

    return [
      {
        key: "error",
        label: "Taxa de Erro Total",
        info: `Proporção de falhas (erros definitivos + retry errors 504) em relação ao total de mensagens recebidas.\n\n🟢 Saudável: < ${t.errorRateWarning}%\n🟡 Atenção: ${t.errorRateWarning}% – ${t.errorRateCritical - 0.1}%\n🔴 Crítico: ≥ ${t.errorRateCritical}%\n\nTaxas altas indicam paralelismo excessivo ou problemas de conectividade com o SAP B1.`,
        value: `${(errorRate * 100).toFixed(1)}%`,
        detail: `${totalFailed} falhas (${totalErrors} erros + ${totalRetries} retries) / ${base} recebidas`,
        status: errorRate < errW ? "ok" : errorRate < errC ? "warning" : "critical",
        rec: errorRate >= errC
          ? "Taxa crítica — reduza maxParallelCalls ou verifique conectividade com SAP B1"
          : "Erros acima do normal — verifique logs; erros 504 indicam paralelismo excessivo",
      },
      {
        key: "retry",
        label: "Retry Errors (504)",
        info: `Mensagens que falharam com erro 504 (timeout do SAP B1 Service Layer) e estão sendo reprocessadas.\n\n🟢 Saudável: < ${t.retryRateWarning}%\n🟡 Atenção: ${t.retryRateWarning}% – ${t.retryRateCritical - 0.1}%\n🔴 Crítico: ≥ ${t.retryRateCritical}%\n\nValores altos indicam que o paralelismo está acima da capacidade do SAP B1.`,
        value: totalRetries === 0 ? "Nenhum" : `${(retryRate * 100).toFixed(1)}%`,
        detail: totalRetries === 0
          ? "Sem retry errors no período"
          : `${totalRetries} retry errors · máx. ${maxRetryCount} tentativas`,
        status: retryRate < retW ? "ok" : retryRate < retC ? "warning" : "critical",
        rec: retryRate >= retC
          ? "Muitos timeouts 504 — reduza maxParallelCalls imediatamente ou ative paralelismo dinâmico"
          : "Timeouts 504 detectados — monitore e considere reduzir o paralelismo",
      },
      {
        key: "throughput",
        label: "Consumo da Fila",
        info: `Percentual de mensagens recebidas que foram efetivamente processadas no período.\n\n🟢 Saudável: ≥ ${t.throughputCritical}%\n🟡 Atenção: ${t.throughputWarning}% – ${t.throughputCritical - 0.1}%\n🔴 Crítico: < ${t.throughputWarning}%\n\nValores baixos indicam backlog — mensagens chegam mais rápido do que são processadas.`,
        value: `${Math.min(100, (throughput * 100)).toFixed(1)}%`,
        detail: `${totalProcessed} de ${totalReceived} mensagens`,
        status: throughput >= thrOk ? "ok" : throughput >= thrW ? "warning" : "critical",
        rec: "Worker com backlog — aumente maxParallelCalls ou investigue erros bloqueantes",
      },
      {
        key: "lock",
        label: "Pressão de Lock",
        info: `Percentual de ciclos de polling em que foram detectados locks ativos no HANA (tabelas OINV, OCRD, etc.).\n\n🟢 Saudável: < ${t.lockRateWarning}%\n🟡 Atenção: ${t.lockRateWarning}% – ${t.lockRateCritical - 1}%\n🔴 Crítico: ≥ ${t.lockRateCritical}%\n\nValores altos indicam contenção frequente no banco de dados SAP B1, o que pode causar erros 504 e atrasos.`,
        value: `${(lockRate * 100).toFixed(0)}%`,
        detail: `${ciclosComLock} de ${totalCiclos} ciclos com lock`,
        status: lockRate < lckW ? "ok" : lockRate < lckC ? "warning" : "critical",
        rec: lockRate >= lckC
          ? "Alta contenção no HANA — ative paralelismo dinâmico e reduza maxParallelCalls"
          : "Locks frequentes — habilite pauseMessagesOnLock e enableInvoiceLockCheck",
      },
      {
        key: "parallel",
        label: "Estab. Paralelismo",
        info: `Número de vezes que o valor de paralelismo mudou entre ciclos consecutivos.\n\n🟢 Saudável: ≤ ${t.parallelChgWarning} ajustes\n🟡 Atenção: ${t.parallelChgWarning + 1} – ${t.parallelChgCritical} ajustes\n🔴 Crítico: > ${t.parallelChgCritical} ajustes\n\nMuitos ajustes indicam que o sistema está oscilando entre contenção e normalidade com frequência.`,
        value: totalParallelChg === 0 ? "Fixo" : `${totalParallelChg} ajustes`,
        detail: totalParallelChg === 0
          ? (minParallel != null ? `Estável em ${minParallel} threads` : "Sem dados de paralelismo")
          : `Variou de ${minParallel} a ${maxParallel} threads`,
        status: totalParallelChg <= pchW ? "ok" : totalParallelChg <= pchC ? "warning" : "critical",
        rec: "Sistema reajustando com frequência — pode indicar contenção recorrente no HANA",
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, counters, locks, thresholds]);

  if (!indicators) return null;

  const criticals = indicators.filter(i => i.status === "critical").length;
  const warnings  = indicators.filter(i => i.status === "warning").length;
  const overall   = criticals > 0 ? "critical" : warnings > 0 ? "warning" : "ok";
  const recs      = indicators.filter(i => i.status !== "ok");

  const STATUS_LABEL = { ok: "SAUDÁVEL", warning: "ATENÇÃO", critical: "CRÍTICO" };
  const STATUS_ICON  = { ok: "✓", warning: "⚠", critical: "✕" };
  const STATUS_MSG   = {
    ok:       "Integração operando dentro dos parâmetros ideais",
    warning:  `${warnings} indicador${warnings > 1 ? "es" : ""} fora do ideal — acompanhe`,
    critical: `${criticals} problema${criticals > 1 ? "s" : ""} crítico${criticals > 1 ? "s" : ""} detectado${criticals > 1 ? "s" : ""} — ação necessária`,
  };

  return (
    <HealthCard status={overall}>
      <HealthHeader status={overall}>
        <HealthBadge status={overall}>
          <span>{STATUS_ICON[overall]}</span>
          {STATUS_LABEL[overall]}
        </HealthBadge>
        <HealthHeaderText>{STATUS_MSG[overall]}</HealthHeaderText>
      </HealthHeader>

      <HealthGrid>
        {indicators.map(ind => (
          <HealthIndicator key={ind.key} status={ind.status}>
            <HealthIndTop>
              <StatusDot status={ind.status} />
              <HealthIndLabel>{ind.label}</HealthIndLabel>
              <HealthInfoTip text={ind.info} />
            </HealthIndTop>
            <HealthIndValue status={ind.status}>{ind.value}</HealthIndValue>
            <HealthIndDetail>{ind.detail}</HealthIndDetail>
          </HealthIndicator>
        ))}
      </HealthGrid>

      {recs.length > 0 && (
        <HealthRecs>
          <HealthRecsTitle>Recomendações</HealthRecsTitle>
          {recs.map(ind => (
            <HealthRecRow key={ind.key} status={ind.status}>
              <StatusDot status={ind.status} size="small" />
              <span><strong>{ind.label}:</strong> {ind.rec}</span>
            </HealthRecRow>
          ))}
        </HealthRecs>
      )}
    </HealthCard>
  );
}

// ─── WORKER CONFIG SUMMARY CARD ───────────────────────────────
const CFG_BOOL_LABELS = {
  enableLogs:              { label: "Logs",              color: "#27ae60", info: "Habilita ou desabilita os logs de operação do worker. Quando inativo, apenas erros críticos são registrados. Útil para reduzir ruído em produção quando o worker está estável." },
  enableNotifications:     { label: "Notificações",      color: "#667eea", info: "Quando ativo, o worker envia notificações (erros críticos, lock prolongado, falhas de retry) para o canal configurado. Desative apenas temporariamente durante manutenções." },
  enableInvoiceLockCheck:  { label: "Verificar locks",   color: "#e74c3c", info: "Ativa a consulta de bloqueios nas tabelas do HANA (M_BLOCKED_TRANSACTIONS) a cada ciclo. Detecta locks em tabelas críticas como OINV e OCRD. Necessário para o paralelismo dinâmico e a pausa de mensagens funcionarem." },
  pauseMessagesOnLock:     { label: "Pausar em lock",    color: "#e74c3c", info: "Quando uma empresa tem lock detectado no HANA, suas mensagens na fila SQS são pausadas temporariamente via VisibilityTimeout. Evita reprocessamentos inúteis em bases bloqueadas." },
  enableDynamicParallelism:{ label: "Paralel. dinâmico", color: "#667eea", info: "Ativa o controle adaptativo de paralelismo. O sistema aumenta ou reduz automaticamente o número de chamadas paralelas com base na detecção de locks e erros 504. Começa no máximo e reduz progressivamente em caso de contenção." },
};

function WorkerConfigCard({ workerConfig, onConfigure }) {
  if (!workerConfig) return null;

  const BoolItem = ({ field }) => {
    const meta = CFG_BOOL_LABELS[field];
    const on = !!workerConfig[field];
    return (
      <CfgItem>
        <CfgDot $on={on} color={meta.color} />
        <CfgItemLabel>{meta.label}</CfgItemLabel>
        <HealthInfoTip text={meta.info} />
        <CfgItemVal $on={on}>{on ? "Ativo" : "Inativo"}</CfgItemVal>
      </CfgItem>
    );
  };

  const NumItem = ({ label, value, info }) => (
    <CfgItem>
      <CfgItemLabel>{label}</CfgItemLabel>
      <HealthInfoTip text={info} />
      <CfgItemNum>{value}</CfgItemNum>
    </CfgItem>
  );

  return (
    <CfgCard>
      <CfgCardHeader>
        <CfgCardTitle>⚙ Configuração Ativa do Worker</CfgCardTitle>
        <CfgCardHint>Reflete as configurações salvas para este grupo</CfgCardHint>
        <CfgEditBtn onClick={onConfigure}>
          <Settings style={{ fontSize: 14, marginRight: 5 }} />
          Configurar
        </CfgEditBtn>
      </CfgCardHeader>
      <CfgGrid>
        <CfgSection>
          <CfgSectionTitle color="#27ae60">Geral</CfgSectionTitle>
          <BoolItem field="enableLogs" />
          <BoolItem field="enableNotifications" />
        </CfgSection>

        <CfgSection>
          <CfgSectionTitle color="#e74c3c">Locks</CfgSectionTitle>
          <BoolItem field="enableInvoiceLockCheck" />
          <BoolItem field="pauseMessagesOnLock" />
          <NumItem
            label="Pausa"
            value={`${workerConfig.pauseVisibilitySeconds ?? "—"}s`}
            info="Tempo em segundos que as mensagens de uma empresa com lock ficam invisíveis na fila (não serão consumidas). Após este período, as mensagens voltam e o worker tenta novamente. Padrão: 300s (5 min)."
          />
          <NumItem
            label="Threshold"
            value={`${workerConfig.minLockSecondsThreshold ?? "—"}s`}
            info="Tempo mínimo em segundos que um lock precisa ter para ser considerado problemático. Locks mais curtos são ignorados. Valores menores (ex: 7s) tornam o sistema mais sensível; valores maiores (ex: 30s) evitam reações a locks transitórios normais."
          />
        </CfgSection>

        <CfgSection>
          <CfgSectionTitle color="#667eea">Paralelismo</CfgSectionTitle>
          <BoolItem field="enableDynamicParallelism" />
          <NumItem
            label="Mínimo"
            value={`${workerConfig.minParallelCalls ?? "—"} threads`}
            info="Piso mínimo de paralelismo quando o controle dinâmico está reduzindo. O worker nunca processa menos que este valor simultaneamente, mesmo sob lock severo."
          />
          <NumItem
            label="Máximo"
            value={`${workerConfig.maxParallelCalls ?? "—"} threads`}
            info="Número máximo de mensagens processadas simultaneamente por ciclo. Valores altos aumentam a throughput mas elevam o risco de erros 504 e locks no HANA. Recomendado: entre 5 e 15."
          />
        </CfgSection>

        <CfgSection>
          <CfgSectionTitle color="#0891b2">Dinâmico — Aumento</CfgSectionTitle>
          <NumItem
            label="Incremento"
            value={`+${workerConfig.parallelismIncreaseStep ?? "—"}`}
            info="Quantidade de chamadas adicionadas ao paralelismo a cada aumento. Valores maiores fazem a recuperação ser mais rápida após um período estável."
          />
          <NumItem
            label="Cooldown"
            value={`${workerConfig.parallelismIncreaseCooldown ?? "—"} ciclos`}
            info="Número de ciclos consecutivos sem erros ou locks necessários antes de aumentar o paralelismo. Valores menores = recuperação mais rápida; valores maiores = mais conservador."
          />
        </CfgSection>

        <CfgSection>
          <CfgSectionTitle color="#e67e22">Dinâmico — Redução</CfgSectionTitle>
          <NumItem
            label="Lock leve"
            value={`-${workerConfig.parallelismDecreaseStepLight ?? "—"}`}
            info="Quantidade removida do paralelismo quando detectado um lock com duração menor que 10 segundos no HANA."
          />
          <NumItem
            label="Lock moderado"
            value={`-${workerConfig.parallelismDecreaseStepModerate ?? "—"}`}
            info="Quantidade removida do paralelismo quando detectado um lock com duração entre 10 e 30 segundos no HANA."
          />
          <NumItem
            label="Lock severo/504"
            value={`-${workerConfig.parallelismDecreaseStepSevere ?? "—"}`}
            info="Quantidade removida do paralelismo quando detectado um lock acima de 30 segundos no HANA, ou quando ocorre erro 504 do SAP B1 Service Layer."
          />
        </CfgSection>

        <CfgSection>
          <CfgSectionTitle color="#f39c12">Retentativas</CfgSectionTitle>
          <NumItem
            label="Máx. retries"
            value={workerConfig.maxRetries === 0 ? "Sem limite" : `${workerConfig.maxRetries ?? "—"} tentativas`}
            info="Número máximo de tentativas para mensagens com erro 504. 0 = sem limite (fica na fila indefinidamente). Valores como 3 ou 5 movem a mensagem para dead-letter após N falhas."
          />
        </CfgSection>
      </CfgGrid>
    </CfgCard>
  );
}

// ─── OVERVIEW TAB ────────────────────────────────────────────
function OverviewTab({ grupoId, from, to, setFrom, setTo, workerConfig, onConfigure, refreshInterval }) {
  const [summary,  setSummary]  = useState([]);
  const [locks,       setLocks]       = useState([]);
  const [counters,    setCounters]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [maxChart,    setMaxChart]    = useState(null);
  const [lockPage,    setLockPage]    = useState(0);
  const LOCK_PAGE_SIZE = 5;

  const fetchData = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const [cntRes, sumRes, lockRes] = await Promise.allSettled([
        fetchClient.get(`/worker/${grupoId}/metrics/counters?from=${toISO(from)}&to=${toISO(to)}`),
        fetchClient.get(`/worker/${grupoId}/metrics/summary?from=${toISO(from)}&to=${toISO(to)}`),
        fetchClient.get(`/worker/${grupoId}/metrics/locks?from=${toISO(from)}&to=${toISO(to)}`),
      ]);
      setCounters(cntRes.status  === "fulfilled" ? cntRes.value.data  || null : null);
      setSummary(sumRes.status   === "fulfilled" ? sumRes.value.data   || []   : []);
      setLocks(lockRes.status    === "fulfilled" ? lockRes.value.data  || []   : []);
    } catch {
      message.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [grupoId, from, to]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSummary([]); setLocks([]); setCounters(null); fetchData(); }, [grupoId]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [fetchData, refreshInterval]);

  const kpis = useMemo(() => {
    const processed = counters?.messages?.processed ?? 0;
    const errors    = (counters?.messages?.errors   ?? 0) + (counters?.retries?.total ?? 0);
    const received  = counters?.messages?.received  ?? 0;
    // Soma duração dos locks resolvidos + tempo dos locks ainda ativos
    const resolvedIds = new Set(locks.filter(l => l.eventType === "LOCK_RESOLVED").map(l => l.companyId));
    const lockTimeResolved = locks
      .filter(l => l.eventType === "LOCK_RESOLVED")
      .reduce((a, l) => a + (l.durationSeconds || 0), 0);
    const lockTimeActive = locks
      .filter(l => l.eventType === "LOCK_DETECTED" && !resolvedIds.has(l.companyId))
      .reduce((a, l) => a + (l.oldestBlockedSeconds || 0), 0);
    const lockTime = lockTimeResolved + lockTimeActive || summary.reduce((a, s) => a + (s.lockDurationSeconds || 0), 0);
    const sortedSum = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const pVals = sortedSum.map(s => s.parallelism).filter(v => v != null);
    let parallelChanges = 0;
    for (let i = 1; i < pVals.length; i++) { if (pVals[i] !== pVals[i - 1]) parallelChanges++; }
    const periodMinutes = Math.max(1, (new Date(to) - new Date(from)) / 60000);
    const avgPerMin = (processed / periodMinutes).toFixed(2);
    return { processed, errors, successRate: received > 0 ? ((processed / received) * 100).toFixed(1) : "—", lockTime, parallelChanges, avgPerMin };
  }, [summary, counters, locks, from, to]);

  const activeLocks = useMemo(() => {
    const detected = locks.filter((l) => l.eventType === "LOCK_DETECTED");
    const resolved = new Set(locks.filter((l) => l.eventType === "LOCK_RESOLVED").map((l) => l.companyId));
    return detected.filter((l) => !resolved.has(l.companyId));
  }, [locks]);

  const chartData = useMemo(() => {
    if (!summary.length) return [["Hora", "Processados", "Erros"]];
    const sorted = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const rows = [["Hora", "Processados", "Erros"]];
    sorted.forEach((s) => {
      const h = new Date(s.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      rows.push([h, s.messagesProcessed || 0, s.messagesError || 0]);
    });
    return rows;
  }, [summary]);

  const exportOverview = () => {
    const sorted = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    exportToCSV(sorted, [
      { label: "Timestamp",   key: "timestamp" },
      { label: "Processados", key: "messagesProcessed" },
      { label: "Recebidos",   key: "messagesReceived" },
      { label: "Erros",       key: "messagesError" },
      { label: "Lock (s)",    key: "lockDurationSeconds" },
      { label: "Paralelismo", key: "parallelism" },
    ], `visao-geral-${grupoId}-${from.slice(0,10)}.csv`);
  };

  if (loading && !summary.length) return <Loader />;

  return (
    <TabContent>
      <FilterBar from={from} to={to} onFrom={setFrom} onTo={setTo} onRefresh={fetchData} loading={loading}>
        <ExportBtn onClick={exportOverview} disabled={!summary.length} title="Exportar CSV">
          <GetApp style={{ fontSize: 16, marginRight: 4 }} />CSV
        </ExportBtn>
      </FilterBar>
      <WorkerConfigCard workerConfig={workerConfig} onConfigure={onConfigure} />
      <KpiGrid>
        <KpiCard color={PRIMARY}>
          <KpiValue>{formatAbbrValue(counters?.messages?.processed ?? kpis.processed)}</KpiValue>
          <KpiLabel>Mensagens Processadas</KpiLabel>
        </KpiCard>
        <KpiCard color={WARNING}>
          <KpiValue>{formatAbbrValue(counters?.messages?.received ?? 0)}</KpiValue>
          <KpiLabel>Mensagens Recebidas</KpiLabel>
        </KpiCard>
        <KpiCard color={DANGER}>
          <KpiValue>{formatAbbrValue(counters?.messages?.errors ?? 0)}</KpiValue>
          <KpiLabel>Erros Definitivos</KpiLabel>
        </KpiCard>
        <KpiCard color="#e67e22">
          <KpiValue>{formatAbbrValue(counters?.retries?.total ?? 0)}</KpiValue>
          <KpiLabel>Retry Errors</KpiLabel>
        </KpiCard>
        <KpiCard color="#8e44ad">
          <KpiValue>{formatAbbrValue(counters?.locks?.total ?? 0)}</KpiValue>
          <KpiLabel>Locks</KpiLabel>
        </KpiCard>
        <KpiCard color="#0891b2">
          <KpiValue>{counters?.cycles?.parallelism ?? "—"}</KpiValue>
          <KpiLabel>Paralelismo Atual</KpiLabel>
        </KpiCard>
        <KpiCard color={SUCCESS}>
          <KpiValue>{kpis.successRate}%</KpiValue>
          <KpiLabel>Taxa de Sucesso</KpiLabel>
        </KpiCard>
        <KpiCard color="#64748b">
          <KpiValue>{formatAbbrValue(counters?.cycles?.total ?? 0)}</KpiValue>
          <KpiLabel>Ciclos</KpiLabel>
        </KpiCard>
      </KpiGrid>

      <HealthPanel summary={summary} counters={counters} locks={locks} thresholds={workerConfig?.healthThresholds} />

      {chartData.length > 1 && (
        <SectionCard>
          <CardHeaderRow>
            <ChartTitle>Mensagens por Hora</ChartTitle>
            <MaximizeBtn
              onClick={() => setMaxChart(maxChart === "overview/hourly" ? null : "overview/hourly")}
              title={maxChart === "overview/hourly" ? "Restaurar" : "Maximizar gráfico"}
            >
              {maxChart === "overview/hourly"
                ? <FullscreenExit style={{ fontSize: 20 }} />
                : <Fullscreen style={{ fontSize: 20 }} />}
            </MaximizeBtn>
          </CardHeaderRow>
          <Chart
            chartType="LineChart"
            width="100%"
            height="280px"
            data={chartData}
            options={{
              colors: [PRIMARY, DANGER],
              legend: { position: "bottom" },
              chartArea: { width: "80%", height: "65%" },
              hAxis: { title: "Hora", slantedText: true, slantedTextAngle: 45 },
              vAxis: { title: "Mensagens", minValue: 0 },
              animation: { startup: true, duration: 800 },
            }}
          />
        </SectionCard>
      )}

      {maxChart === "overview/hourly" && (
        <MaxChartOverlay onClick={() => setMaxChart(null)}>
          <MaxChartCard onClick={(e) => e.stopPropagation()}>
            <CardHeaderRow>
              <ChartTitle>Mensagens por Hora</ChartTitle>
              <MaximizeBtn onClick={() => setMaxChart(null)} title="Restaurar">
                <FullscreenExit style={{ fontSize: 20 }} />
              </MaximizeBtn>
            </CardHeaderRow>
            <Chart
              chartType="LineChart"
              width="100%"
              height="520px"
              data={chartData}
              options={{
                colors: [PRIMARY, DANGER],
                legend: { position: "bottom" },
                chartArea: { width: "80%", height: "70%" },
                hAxis: { title: "Hora", slantedText: true, slantedTextAngle: 45 },
                vAxis: { title: "Mensagens", minValue: 0 },
                animation: { startup: true, duration: 800 },
              }}
            />
          </MaxChartCard>
        </MaxChartOverlay>
      )}

      {activeLocks.length > 0 && (
        <SectionCard>
          <SectionTitle>⚠ Empresas com Lock Ativo ({activeLocks.length})</SectionTitle>
          {activeLocks.slice(lockPage * LOCK_PAGE_SIZE, (lockPage + 1) * LOCK_PAGE_SIZE).map((l, i) => (
            <ActiveLockRow key={i}>
              <LockBadge active>ATIVO</LockBadge>
              <span><strong>{l.schemaName}</strong> — {l.blockedCount} bloqueios — {l.oldestBlockedSeconds}s</span>
            </ActiveLockRow>
          ))}
          {activeLocks.length > LOCK_PAGE_SIZE && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <button
                onClick={() => setLockPage(p => Math.max(0, p - 1))}
                disabled={lockPage === 0}
                style={{ padding: "2px 10px", cursor: lockPage === 0 ? "not-allowed" : "pointer" }}
              >‹</button>
              <span style={{ fontSize: 12, color: "#666" }}>
                {lockPage + 1} / {Math.ceil(activeLocks.length / LOCK_PAGE_SIZE)}
              </span>
              <button
                onClick={() => setLockPage(p => Math.min(Math.ceil(activeLocks.length / LOCK_PAGE_SIZE) - 1, p + 1))}
                disabled={lockPage >= Math.ceil(activeLocks.length / LOCK_PAGE_SIZE) - 1}
                style={{ padding: "2px 10px", cursor: lockPage >= Math.ceil(activeLocks.length / LOCK_PAGE_SIZE) - 1 ? "not-allowed" : "pointer" }}
              >›</button>
            </div>
          )}
        </SectionCard>
      )}

    </TabContent>
  );
}

// ─── LOCKS TAB ───────────────────────────────────────────────
function LocksTab({ grupoId, from, to, setFrom, setTo, refreshInterval }) {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const { data } = await fetchClient.get(
        `/worker/${grupoId}/metrics/locks?from=${toISO(from)}&to=${toISO(to)}`
      );
      setLocks(data || []);
    } catch {
      message.error("Erro ao carregar locks");
    } finally {
      setLoading(false);
    }
  }, [grupoId, from, to]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocks([]); fetchData(); }, [grupoId]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [fetchData, refreshInterval]);

  const detected = useMemo(() => locks.filter((l) => l.eventType === "LOCK_DETECTED"), [locks]);
  const resolvedMap = useMemo(() => {
    const m = {};
    locks.filter((l) => l.eventType === "LOCK_RESOLVED").forEach((l) => { m[l.companyId] = l; });
    return m;
  }, [locks]);

  const topTables = useMemo(() => {
    const agg = {};
    detected.forEach((l) =>
      (l.topTables || []).forEach((t) => {
        if (!agg[t.tableName]) agg[t.tableName] = { count: 0, maxSec: 0 };
        agg[t.tableName].count += t.blockedCount;
        agg[t.tableName].maxSec = Math.max(agg[t.tableName].maxSec, t.oldestBlockedSeconds);
      })
    );
    return Object.entries(agg).sort((a, b) => b[1].count - a[1].count).slice(0, 10);
  }, [detected]);

  const exportLocks = () => {
    const rows = detected.map((l) => ({
      ...l,
      resolved: resolvedMap[l.companyId] ? "SIM" : "NÃO",
      durationSeconds: resolvedMap[l.companyId]?.durationSeconds ?? "",
    }));
    exportToCSV(rows, [
      { label: "Timestamp",       key: "timestamp" },
      { label: "Empresa",         key: "schemaName" },
      { label: "Bloqueios",       key: "blockedCount" },
      { label: "Mais antigo (s)", key: "oldestBlockedSeconds" },
      { label: "Resolvido",       key: "resolved" },
      { label: "Duração (s)",     key: "durationSeconds" },
    ], `locks-${grupoId}-${from.slice(0,10)}.csv`);
  };

  if (loading && !locks.length) return <Loader />;

  return (
    <TabContent>
      <FilterBar from={from} to={to} onFrom={setFrom} onTo={setTo} onRefresh={fetchData} loading={loading}>
        <ExportBtn onClick={exportLocks} disabled={!detected.length} title="Exportar CSV">
          <GetApp style={{ fontSize: 16, marginRight: 4 }} />CSV
        </ExportBtn>
      </FilterBar>

      <SectionCard>
        <SectionTitle>Timeline de Locks ({detected.length})</SectionTitle>
        {detected.length === 0 && <EmptyMsg>Nenhum lock detectado no período.</EmptyMsg>}
        {detected.map((l, i) => {
          const resolved = resolvedMap[l.companyId];
          const isOpen = expanded[i];
          return (
            <LockTimelineItem key={i}>
              <LockItemHeader onClick={() => setExpanded((p) => ({ ...p, [i]: !p[i] }))}>
                <LockBadge active={!resolved}>{resolved ? "✅ RESOLVIDO" : "🔴 ATIVO"}</LockBadge>
                <LockMeta>
                  <strong>{l.schemaName}</strong>
                  <span>{new Date(l.timestamp).toLocaleString("pt-BR")}</span>
                  <span>{l.blockedCount} bloqueios · {l.oldestBlockedSeconds}s mais antigo</span>
                  {resolved && <span>Duração total: {resolved.durationSeconds}s</span>}
                </LockMeta>
                <ExpandArrow open={isOpen}>▼</ExpandArrow>
              </LockItemHeader>
              {isOpen && (
                <LockDetails>
                  {(l.topTables || []).length > 0 && (
                    <div>
                      <DetailTitle>Top Tabelas</DetailTitle>
                      {l.topTables.map((t, j) => (
                        <DetailRow key={j}>
                          <Tag>{t.tableName}</Tag>
                          <span>{t.blockedCount} bloqueios · {t.oldestBlockedSeconds}s · média {t.avgBlockedSeconds}s</span>
                        </DetailRow>
                      ))}
                    </div>
                  )}
                  {(l.topBlockers || []).length > 0 && (
                    <div>
                      <DetailTitle>Top Conexões Bloqueadoras</DetailTitle>
                      {l.topBlockers.map((b, j) => (
                        <DetailRow key={j}>
                          <Tag>Conn {b.connectionId}</Tag>
                          <span>{b.blockedCount} bloqueios · {b.oldestBlockedSeconds}s</span>
                        </DetailRow>
                      ))}
                    </div>
                  )}
                </LockDetails>
              )}
            </LockTimelineItem>
          );
        })}
      </SectionCard>

      {topTables.length > 0 && (
        <SectionCard>
          <SectionTitle>Top Tabelas Bloqueadas no Período</SectionTitle>
          <SimpleTable>
            <thead>
              <tr><Th>Tabela</Th><Th>Total Bloqueios</Th><Th>Máx Segundos</Th></tr>
            </thead>
            <tbody>
              {topTables.map(([name, v]) => (
                <Tr key={name}>
                  <Td><Tag>{name}</Tag></Td>
                  <Td>{v.count}</Td>
                  <Td>{v.maxSec}s</Td>
                </Tr>
              ))}
            </tbody>
          </SimpleTable>
        </SectionCard>
      )}
    </TabContent>
  );
}

// ─── PERFORMANCE TAB ─────────────────────────────────────────
function PerformanceTab({ grupoId, from, to, setFrom, setTo, refreshInterval }) {
  const [summary, setSummary] = useState([]);
  const [parallelism, setParallelism] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxChart, setMaxChart] = useState(null);

  const fetchData = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const [sumRes, parRes] = await Promise.allSettled([
        fetchClient.get(`/worker/${grupoId}/metrics/summary?from=${toISO(from)}&to=${toISO(to)}`),
        fetchClient.get(`/worker/${grupoId}/metrics/parallelism?from=${toISO(from)}&to=${toISO(to)}`),
      ]);
      setSummary(sumRes.status === "fulfilled" ? sumRes.value.data || [] : []);
      setParallelism(parRes.status === "fulfilled" ? parRes.value.data || [] : []);
    } catch {
      message.error("Erro ao carregar performance");
    } finally {
      setLoading(false);
    }
  }, [grupoId, from, to]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSummary([]); setParallelism([]); fetchData(); }, [grupoId]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [fetchData, refreshInterval]);

  const kpis = useMemo(() => {
    if (!summary.length) return { avg: 0, peak: 0, withErrors: 0 };
    const avg = Math.round(summary.reduce((a, s) => a + (s.messagesProcessed || 0), 0) / summary.length);
    const peak = Math.max(...summary.map((s) => s.messagesReceived || 0));
    const withErrors = summary.filter((s) => (s.messagesError || 0) > 0).length;
    return { avg, peak, withErrors };
  }, [summary]);

  // Sorted by timestamp, then labeled
  const barData = useMemo(() => {
    if (!summary.length) return [["Hora", "Processados", "Erros"]];
    const sorted = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const rows = [["Hora", "Processados", "Erros"]];
    sorted.forEach((s) => {
      const h = new Date(s.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      rows.push([h, s.messagesProcessed || 0, s.messagesError || 0]);
    });
    return rows;
  }, [summary]);

  const parallelData = useMemo(() => {
    const combined = [
      ...parallelism.map((p) => ({ ts: new Date(p.timestamp), val: p.toValue })),
      ...summary.filter((s) => s.parallelism != null).map((s) => ({ ts: new Date(s.timestamp), val: s.parallelism })),
    ].sort((a, b) => a.ts - b.ts);
    if (!combined.length) return [["Hora", "Paralelismo"]];
    const rows = [["Hora", "Paralelismo"]];
    combined.forEach((p) => rows.push([p.ts.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }), p.val]));
    return rows;
  }, [parallelism, summary]);

  const exportPerformance = () => {
    const sorted = [...summary].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    exportToCSV(sorted, [
      { label: "Timestamp",   key: "timestamp" },
      { label: "Processados", key: "messagesProcessed" },
      { label: "Recebidos",   key: "messagesReceived" },
      { label: "Erros",       key: "messagesError" },
      { label: "Paralelismo", key: "parallelism" },
    ], `performance-${grupoId}-${from.slice(0,10)}.csv`);
  };

  if (loading && !summary.length) return <Loader />;

  return (
    <TabContent>
      <FilterBar from={from} to={to} onFrom={setFrom} onTo={setTo} onRefresh={fetchData} loading={loading}>
        <ExportBtn onClick={exportPerformance} disabled={!summary.length} title="Exportar CSV">
          <GetApp style={{ fontSize: 16, marginRight: 4 }} />CSV
        </ExportBtn>
      </FilterBar>
      <KpiGrid>
        <KpiCard color={PRIMARY}>
          <KpiValue>{kpis.avg}</KpiValue>
          <KpiLabel>Média por Ciclo</KpiLabel>
        </KpiCard>
        <KpiCard color={SUCCESS}>
          <KpiValue>{kpis.peak}</KpiValue>
          <KpiLabel>Pico de Processamento</KpiLabel>
        </KpiCard>
        <KpiCard color={DANGER}>
          <KpiValue>{kpis.withErrors}</KpiValue>
          <KpiLabel>Ciclos com Erros</KpiLabel>
        </KpiCard>
      </KpiGrid>

      {barData.length > 1 && (
        <SectionCard>
          <CardHeaderRow>
            <ChartTitle>Processados vs Erros por Ciclo</ChartTitle>
            <MaximizeBtn
              onClick={() => setMaxChart(maxChart === "perf/bar" ? null : "perf/bar")}
              title={maxChart === "perf/bar" ? "Restaurar" : "Maximizar gráfico"}
            >
              {maxChart === "perf/bar"
                ? <FullscreenExit style={{ fontSize: 20 }} />
                : <Fullscreen style={{ fontSize: 20 }} />}
            </MaximizeBtn>
          </CardHeaderRow>
          <Chart
            chartType="ColumnChart"
            width="100%"
            height="300px"
            data={barData}
            options={{
              colors: [SUCCESS, DANGER],
              isStacked: true,
              legend: { position: "bottom" },
              chartArea: { width: "80%", height: "60%" },
              hAxis: { slantedText: true, slantedTextAngle: 45, textStyle: { fontSize: 11 } },
              vAxis: { title: "Mensagens", minValue: 0 },
              animation: { startup: true, duration: 800 },
            }}
          />
        </SectionCard>
      )}

      {parallelData.length > 1 && (
        <SectionCard>
          <CardHeaderRow>
            <ChartTitle>Paralelismo ao Longo do Tempo</ChartTitle>
            <MaximizeBtn
              onClick={() => setMaxChart(maxChart === "perf/parallel" ? null : "perf/parallel")}
              title={maxChart === "perf/parallel" ? "Restaurar" : "Maximizar gráfico"}
            >
              {maxChart === "perf/parallel"
                ? <FullscreenExit style={{ fontSize: 20 }} />
                : <Fullscreen style={{ fontSize: 20 }} />}
            </MaximizeBtn>
          </CardHeaderRow>
          <Chart
            chartType="LineChart"
            width="100%"
            height="220px"
            data={parallelData}
            options={{
              colors: [WARNING],
              legend: { position: "none" },
              chartArea: { width: "80%", height: "65%" },
              hAxis: { slantedText: true, slantedTextAngle: 45, textStyle: { fontSize: 11 } },
              vAxis: { title: "Threads", minValue: 0 },
              animation: { startup: true, duration: 800 },
            }}
          />
        </SectionCard>
      )}

      {maxChart && (
        <MaxChartOverlay onClick={() => setMaxChart(null)}>
          <MaxChartCard onClick={(e) => e.stopPropagation()}>
            <CardHeaderRow>
              <ChartTitle>
                {maxChart === "perf/bar"
                  ? "Processados vs Erros por Ciclo"
                  : "Paralelismo ao Longo do Tempo"}
              </ChartTitle>
              <MaximizeBtn onClick={() => setMaxChart(null)} title="Restaurar">
                <FullscreenExit style={{ fontSize: 20 }} />
              </MaximizeBtn>
            </CardHeaderRow>
            {maxChart === "perf/bar" ? (
              <Chart
                chartType="ColumnChart"
                width="100%"
                height="520px"
                data={barData}
                options={{
                  colors: [SUCCESS, DANGER],
                  isStacked: true,
                  legend: { position: "bottom" },
                  chartArea: { width: "80%", height: "70%" },
                  hAxis: { slantedText: true, slantedTextAngle: 45, textStyle: { fontSize: 12 } },
                  vAxis: { title: "Mensagens", minValue: 0 },
                  animation: { startup: true, duration: 800 },
                }}
              />
            ) : (
              <Chart
                chartType="LineChart"
                width="100%"
                height="520px"
                data={parallelData}
                options={{
                  colors: [WARNING],
                  legend: { position: "none" },
                  chartArea: { width: "80%", height: "70%" },
                  hAxis: { slantedText: true, slantedTextAngle: 45, textStyle: { fontSize: 12 } },
                  vAxis: { title: "Threads", minValue: 0 },
                  animation: { startup: true, duration: 800 },
                }}
              />
            )}
          </MaxChartCard>
        </MaxChartOverlay>
      )}
    </TabContent>
  );
}

// ─── ERRORS TAB ──────────────────────────────────────────────
function ErrorsTab({ grupoId, from, to, setFrom, setTo, refreshInterval }) {
  const [counters, setCounters] = useState(null);
  const [loading,  setLoading]  = useState(false);

  const fetchData = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const { data } = await fetchClient.get(`/worker/${grupoId}/metrics/counters?from=${toISO(from)}&to=${toISO(to)}`);
      setCounters(data || null);
    } catch {
      message.error("Erro ao carregar erros");
    } finally {
      setLoading(false);
    }
  }, [grupoId, from, to]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCounters(null); fetchData(); }, [grupoId]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [fetchData, refreshInterval]);

  if (loading && !counters) return <Loader />;

  return (
    <TabContent>
      <FilterBar from={from} to={to} onFrom={setFrom} onTo={setTo} onRefresh={fetchData} loading={loading} />
      <KpiGrid>
        <KpiCard color={DANGER}>
          <KpiValue>{formatAbbrValue(counters?.errors?.total ?? 0)}</KpiValue>
          <KpiLabel>Erros</KpiLabel>
        </KpiCard>
        <KpiCard color={WARNING}>
          <KpiValue>{formatAbbrValue(counters?.retries?.total ?? 0)}</KpiValue>
          <KpiLabel>Retry Errors (504)</KpiLabel>
        </KpiCard>
      </KpiGrid>
    </TabContent>
  );
}

// ─── CONSOLIDATED TAB ────────────────────────────────────────
function groupKpis(g) {
  const c = g.counters;
  const processed        = c?.messages?.processed    ?? 0;
  const received         = c?.messages?.received     ?? 0;
  const defErrors        = c?.messages?.errors       ?? 0;
  const retriesTotal     = c?.retries?.total         ?? 0;
  const totalErrors      = defErrors + retriesTotal;
  const locksTotal       = c?.locks?.total           ?? 0;
  const cyclesTotal      = c?.cycles?.total          ?? 0;
  const parallelismChanges = c?.cycles?.parallelismChanges ?? null;
  const lockRate         = cyclesTotal > 0 ? locksTotal  / cyclesTotal : 0;
  const base             = Math.max(received, processed + totalErrors) || 1;
  const errorRate        = totalErrors  / base;
  const retryRate        = retriesTotal / base;
  const throughput       = received > 0 ? processed / received : 1;
  return { processed, received, totalErrors, locksTotal, lockRate, errorRate, retryRate, throughput, parallelismChanges };
}

function GroupSummaryCard({ groupData, onSelect }) {
  const { id } = groupData;
  const { processed, totalErrors, errorRate, retryRate, throughput, locksTotal, lockRate, parallelismChanges } = groupKpis(groupData);
  const t = mergeThresholds(groupData.healthThresholds);

  const rank = s => s === "critical" ? 2 : s === "warning" ? 1 : 0;
  const worst = (...statuses) => statuses.reduce((a, b) => rank(a) >= rank(b) ? a : b, "ok");

  const errW  = t.errorRateWarning   / 100;
  const errC  = t.errorRateCritical  / 100;
  const retW  = t.retryRateWarning   / 100;
  const retC  = t.retryRateCritical  / 100;
  const thrOk = t.throughputCritical / 100;
  const thrW  = t.throughputWarning  / 100;
  const lckW  = t.lockRateWarning    / 100;
  const lckC  = t.lockRateCritical   / 100;

  const errorStatus     = errorRate  >= errC  ? "critical" : errorRate  >= errW  ? "warning" : "ok";
  const retryStatus     = retryRate  >= retC  ? "critical" : retryRate  >= retW  ? "warning" : "ok";
  const throughputStatus= throughput <  thrW  ? "critical" : throughput <  thrOk ? "warning" : "ok";
  const lockStatus      = lockRate   >= lckC  ? "critical" : lockRate   >= lckW  ? "warning" : "ok";
  const parallelStatus  = parallelismChanges === null ? "ok"
    : parallelismChanges > t.parallelChgCritical ? "critical"
    : parallelismChanges > t.parallelChgWarning  ? "warning" : "ok";
  const status = worst(errorStatus, retryStatus, throughputStatus, lockStatus, parallelStatus);
  const statusLabel = { ok: "SAUDÁVEL", warning: "ATENÇÃO", critical: "CRÍTICO" }[status];
  const statusColor = { ok: SUCCESS, warning: WARNING, critical: DANGER }[status];

  return (
    <GSummaryCard onClick={onSelect} status={status}>
      <GSummaryHeader>
        <GSummaryId>{id}</GSummaryId>
        <GStatusBadge color={statusColor}>{statusLabel}</GStatusBadge>
      </GSummaryHeader>
      <GSummaryKpis>
        <GSummaryKpi>
          <GSummaryKpiVal color={PRIMARY}>{formatAbbrValue(processed)}</GSummaryKpiVal>
          <GSummaryKpiLbl>Processados</GSummaryKpiLbl>
        </GSummaryKpi>
        <GSummaryKpi>
          <GSummaryKpiVal color={totalErrors > 0 ? DANGER : "#94a3b8"}>{formatAbbrValue(totalErrors)}</GSummaryKpiVal>
          <GSummaryKpiLbl>Erros</GSummaryKpiLbl>
        </GSummaryKpi>
        <GSummaryKpi>
          <GSummaryKpiVal color={errorRate < errW ? SUCCESS : errorRate < errC ? WARNING : DANGER}>
            {(errorRate * 100).toFixed(1)}%
          </GSummaryKpiVal>
          <GSummaryKpiLbl>Taxa erro</GSummaryKpiLbl>
        </GSummaryKpi>
        <GSummaryKpi>
          <GSummaryKpiVal color={locksTotal > 0 ? DANGER : "#94a3b8"}>{locksTotal}</GSummaryKpiVal>
          <GSummaryKpiLbl>Locks ativos</GSummaryKpiLbl>
        </GSummaryKpi>
      </GSummaryKpis>
      <GSummaryFooter>Ver detalhes →</GSummaryFooter>
    </GSummaryCard>
  );
}

function ConsolidatedTab({ grupos, from, to, setFrom, setTo, refreshInterval, parentGroupFilter = "", onSelectGroup }) {
  const [groupData, setGroupData]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [maxChart, setMaxChart]             = useState(null); // { title, chartType, data, options }
  const [timelineChartType, setTimelineChartType] = useState("LineChart"); // "LineChart" | "ColumnChart"

  // Body-lock: preserves scroll position while overlay is open
  useEffect(() => {
    if (!maxChart) return;
    const y = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${y}px`;
    document.body.style.width    = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
      window.scrollTo(0, y);
    };
  }, [maxChart]);

  const openMaxChart  = (cfg) => setMaxChart(cfg);
  const closeMaxChart = () => setMaxChart(null);

  const fetchAll = useCallback(async () => {
    if (!grupos.length) return;
    setLoading(true);
    try {
      // Fase 1: métricas principais (rápidas, DB)
      const results = await Promise.allSettled(
        grupos.map(async (g) => {
          const [cfgRes, cntRes] = await Promise.allSettled([
            fetchClient.get(`/worker/${g.id}/config`),
            fetchClient.get(`/worker/${g.id}/metrics/counters?from=${toISO(from)}&to=${toISO(to)}`),
          ]);
          const config   = cfgRes.status === "fulfilled" ? cfgRes.value.data?.workerConfig || cfgRes.value.data || {} : {};
          const counters = cntRes.status === "fulfilled" ? cntRes.value.data || null : null;
          return { id: g.id, parentGroup: config.parentGroup || "", healthThresholds: config.healthThresholds, counters };
        })
      );
      const mainData = results.filter(r => r.status === "fulfilled").map(r => r.value);
      setGroupData(mainData);
    } finally {
      setLoading(false);
    }
  }, [grupos, from, to]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [grupos, from, to]);
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(fetchAll, refreshInterval);
    return () => clearInterval(id);
  }, [fetchAll, refreshInterval]);

  const grouped = useMemo(() => {
    const map = {};
    groupData.forEach(g => {
      const key = g.parentGroup || "Sem grupo principal";
      if (!map[key]) map[key] = [];
      map[key].push(g);
    });
    const entries = Object.entries(map).sort((a, b) => {
      if (a[0] === "Sem grupo principal") return 1;
      if (b[0] === "Sem grupo principal") return -1;
      return a[0].localeCompare(b[0]);
    });
    if (parentGroupFilter) return entries.filter(([name]) => name === parentGroupFilter);
    return entries;
  }, [groupData, parentGroupFilter]);

  if (loading && !groupData.length) return <Loader />;

  return (
    <TabContent>
      <FilterBar from={from} to={to} onFrom={setFrom} onTo={setTo} onRefresh={fetchAll} loading={loading} />
      {grouped.length === 0 && <EmptyMsg>Nenhum dado disponível no período</EmptyMsg>}
      {grouped.map(([parentName, gList]) => {
        // ── KPIs do grupo principal ──────────────────────────
        const kpiList = gList.map(g => ({ id: g.id, ...groupKpis(g) }));
        const totalProcessed = kpiList.reduce((a, k) => a + k.processed, 0);
        const totalErrors    = kpiList.reduce((a, k) => a + k.totalErrors, 0);
        const avgErrorRate   = totalProcessed > 0 ? (totalErrors / totalProcessed * 100).toFixed(1) : "0.0";
        const totalLocks     = kpiList.reduce((a, k) => a + k.locksTotal, 0);

        const PALETTE = ["#667eea", "#27ae60", "#e67e22", "#e74c3c", "#8e44ad", "#0891b2"];

        return (
          <ConsolidatedSection key={parentName}>
            {/* Cabeçalho + KPI resumo */}
            <ConsolidatedSectionHeader>
              <ConsolidatedSectionTitle>{parentName}</ConsolidatedSectionTitle>
              <ConsolidatedSummaryKpis>
                <ConsolidatedSummaryKpi color={PRIMARY}>
                  <span>{formatAbbrValue(totalProcessed)}</span>
                  <small>processados</small>
                </ConsolidatedSummaryKpi>
                <ConsolidatedSummaryKpi color={totalErrors > 0 ? DANGER : "#94a3b8"}>
                  <span>{formatAbbrValue(totalErrors)}</span>
                  <small>erros</small>
                </ConsolidatedSummaryKpi>
                <ConsolidatedSummaryKpi color={parseFloat(avgErrorRate) < 5 ? SUCCESS : parseFloat(avgErrorRate) < 15 ? WARNING : DANGER}>
                  <span>{avgErrorRate}%</span>
                  <small>taxa erro</small>
                </ConsolidatedSummaryKpi>
                {totalLocks > 0 && (
                  <ConsolidatedSummaryKpi color={DANGER}>
                    <span>{totalLocks}</span>
                    <small>locks ativos</small>
                  </ConsolidatedSummaryKpi>
                )}
              </ConsolidatedSummaryKpis>
              <ConsolidatedCount>{gList.length} grupo{gList.length !== 1 ? "s" : ""}</ConsolidatedCount>
            </ConsolidatedSectionHeader>

            {/* KPI cards dos grupos — PRIMEIRO */}
            <ConsolidatedGrid>
              {gList.map(g => (
                <GroupSummaryCard key={g.id} groupData={g} onSelect={() => onSelectGroup(g.id)} />
              ))}
            </ConsolidatedGrid>

            {/* Gráfico comparativo por grupo */}
            {gList.length > 1 && totalProcessed > 0 && (
              <ConsolidatedChartRow>
                <SectionCard style={{ flex: 1 }}>
                  <CardHeaderRow>
                    <ChartTitle>Processados vs Erros por Grupo</ChartTitle>
                  </CardHeaderRow>
                  <Chart
                    chartType="ColumnChart"
                    width="100%"
                    height="220px"
                    data={[
                      ["Grupo", "Processados", "Erros"],
                      ...kpiList.map(k => [k.id, k.processed, k.totalErrors]),
                    ]}
                    options={{
                      colors: [PRIMARY, DANGER],
                      legend: { position: "bottom" },
                      chartArea: { width: "75%", height: "60%" },
                      hAxis: { textStyle: { fontSize: 11 } },
                      vAxis: { minValue: 0 },
                      animation: { startup: true, duration: 600 },
                    }}
                  />
                </SectionCard>
              </ConsolidatedChartRow>
            )}
          </ConsolidatedSection>
        );
      })}
      {maxChart && (
        <MaxChartOverlay onClick={closeMaxChart}>
          <MaxChartCard onClick={e => e.stopPropagation()}>
            <CardHeaderRow>
              <ChartTitle>{maxChart.title}</ChartTitle>
              <MaximizeBtn onClick={closeMaxChart} title="Restaurar">
                <FullscreenExit style={{ fontSize: 20 }} />
              </MaximizeBtn>
            </CardHeaderRow>
            <Chart
              chartType={maxChart.chartType}
              width="100%"
              height="520px"
              data={maxChart.data}
              options={maxChart.options}
            />
          </MaxChartCard>
        </MaxChartOverlay>
      )}
    </TabContent>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────
function WorkerDashboard() {
  const history = useHistory();
  const [grupos, setGrupos] = useState([]);
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo]     = useState(DEFAULT_TO);
  const [workerConfig, setWorkerConfig] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30000);
  const [maximized, setMaximized] = useState(false);

  // Parent group filter (header select)
  const [availableParentGroups, setAvailableParentGroups] = useState([]);
  const [groupParentMap, setGroupParentMap] = useState({});
  const [parentGroupFilter, setParentGroupFilter] = useState("");

  // Group detail drill-down
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupTab, setSelectedGroupTab] = useState("overview");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMaximized(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Load all groups
  useEffect(() => {
    fetchClient.get("/comerzzia/getAllGroups")
      .then(({ data }) => setGrupos(data || []))
      .catch(() => {});
  }, []);

  // Build parent group map from configs (lightweight — just to populate the header filter)
  useEffect(() => {
    if (!grupos.length) return;
    Promise.allSettled(grupos.map(g => fetchClient.get(`/worker/${g.id}/config`)))
      .then(results => {
        const pgs = new Set();
        const map = {};
        results.forEach((r, i) => {
          const cfg = r.status === "fulfilled"
            ? (r.value.data?.workerConfig || r.value.data || {}) : {};
          const pg = cfg.parentGroup || "";
          map[grupos[i].id] = pg;
          if (pg) pgs.add(pg);
        });
        setGroupParentMap(map);
        setAvailableParentGroups([...pgs].sort());
      });
  }, [grupos]);

  // Load worker config when drilling into a group
  useEffect(() => {
    if (!selectedGroupId) { setWorkerConfig(null); return; }
    fetchClient.get(`/worker/${selectedGroupId}/config`)
      .then(({ data }) => setWorkerConfig(data.workerConfig || data))
      .catch(() => setWorkerConfig(null));
  }, [selectedGroupId]);

  // Groups visible in the detail selector (same parent group, or all if no filter)
  const groupsForSelector = useMemo(() =>
    !parentGroupFilter ? grupos : grupos.filter(g => groupParentMap[g.id] === parentGroupFilter),
    [grupos, groupParentMap, parentGroupFilter]
  );

  const handleSelectGroup = (id) => {
    setSelectedGroupId(id);
    setSelectedGroupTab("overview");
  };

  return (
    <Container maximized={maximized}>
      <ModernHeader>
        <HeaderLeft>
          <BackBtn onClick={() => history.goBack()}>
            <ArrowBack />
          </BackBtn>
          <HeaderContent>
            <PageTitle>Worker Dashboard</PageTitle>
            <PageSubtitle>
              Monitoramento em tempo real do processador de NF-e ·{" "}
              {refreshInterval > 0
                ? `atualização automática a cada ${REFRESH_OPTIONS.find(o => o.value === refreshInterval)?.label}`
                : "auto-refresh desativado"}
            </PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
        <GrupoWrapper>
          <FilterLabel>Grupo Principal:</FilterLabel>
          <GrupoSelect
            value={parentGroupFilter}
            onChange={(e) => { setParentGroupFilter(e.target.value); setSelectedGroupId(null); }}
          >
            <option value="">Todos</option>
            {availableParentGroups.map(pg => (
              <option key={pg} value={pg}>{pg}</option>
            ))}
          </GrupoSelect>
        </GrupoWrapper>
        <GrupoWrapper>
          <FilterLabel>Auto-refresh:</FilterLabel>
          <RefreshSelect value={refreshInterval} onChange={(e) => setRefreshInterval(Number(e.target.value))}>
            {REFRESH_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </RefreshSelect>
        </GrupoWrapper>
        <MaximizeBtn onClick={() => setMaximized(v => !v)} title={maximized ? "Restaurar (Esc)" : "Maximizar painel"}>
          {maximized ? <FullscreenExit style={{ fontSize: 22 }} /> : <Fullscreen style={{ fontSize: 22 }} />}
        </MaximizeBtn>
      </ModernHeader>

      {selectedGroupId ? (
        // ── Group detail view ──────────────────────────────────────
        <>
          <GroupDetailHeader>
            <BackToConsolidatedBtn onClick={() => setSelectedGroupId(null)}>
              <ArrowBack style={{ fontSize: 16 }} />
              Voltar
            </BackToConsolidatedBtn>
            <GroupDetailTitle>{selectedGroupId}</GroupDetailTitle>
            {groupsForSelector.length > 1 && (
              <GrupoWrapper style={{ marginLeft: "auto" }}>
                <FilterLabel>Grupo:</FilterLabel>
                <GrupoSelect value={selectedGroupId} onChange={(e) => handleSelectGroup(e.target.value)}>
                  {groupsForSelector.map(g => (
                    <option key={g.id} value={g.id}>{g.id}</option>
                  ))}
                </GrupoSelect>
              </GrupoWrapper>
            )}
          </GroupDetailHeader>
          <TabNav>
            {GROUP_DETAIL_TABS.map(t => (
              <TabNavBtn key={t.id} active={selectedGroupTab === t.id} onClick={() => setSelectedGroupTab(t.id)}>
                {t.icon}
                <span>{t.label}</span>
              </TabNavBtn>
            ))}
          </TabNav>
          {selectedGroupTab === "overview"    && <OverviewTab    grupoId={selectedGroupId} from={from} to={to} setFrom={setFrom} setTo={setTo} workerConfig={workerConfig} onConfigure={() => history.push("/comerzzia/grupos")} refreshInterval={refreshInterval} />}
          {selectedGroupTab === "queue"       && <QueueTab       grupoId={selectedGroupId} refreshInterval={refreshInterval} />}
          {selectedGroupTab === "locks"       && <LocksTab       grupoId={selectedGroupId} from={from} to={to} setFrom={setFrom} setTo={setTo} refreshInterval={refreshInterval} />}
          {selectedGroupTab === "performance" && <PerformanceTab grupoId={selectedGroupId} from={from} to={to} setFrom={setFrom} setTo={setTo} refreshInterval={refreshInterval} />}
          {selectedGroupTab === "errors"      && <ErrorsTab      grupoId={selectedGroupId} from={from} to={to} setFrom={setFrom} setTo={setTo} refreshInterval={refreshInterval} />}
        </>
      ) : (
        // ── Consolidated overview (visão geral) ───────────────────
        <ConsolidatedTab
          grupos={grupos}
          from={from}
          to={to}
          setFrom={setFrom}
          setTo={setTo}
          refreshInterval={refreshInterval}
          parentGroupFilter={parentGroupFilter}
          onSelectGroup={handleSelectGroup}
        />
      )}
    </Container>
  );
}

export default WorkerDashboard;

// ─── Layout base ──────────────────────────────────────────────
const Container = styled.div`
  font-family: Mulish, sans-serif;
  box-sizing: border-box;

  ${(p) => p.maximized ? `
    position: fixed;
    inset: 0;
    z-index: 1300;
    background: #f8fafc;
    overflow-y: auto;
    padding: 24px;
  ` : `
    max-width: 1400px;
    margin: 0 auto;
    padding: 32px 24px 48px;
  `}
`;

const MaximizeBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  color: #64748b;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${PRIMARY};
    color: ${PRIMARY};
    background: rgba(102, 126, 234, 0.06);
  }
`;

const ChartTypeToggle = styled.div`
  display: flex;
  gap: 4px;
  margin-right: 6px;
`;

const ChartTypeBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1.5px solid ${p => p.active ? PRIMARY : '#e2e8f0'};
  border-radius: 8px;
  background: ${p => p.active ? `rgba(102,126,234,0.10)` : 'white'};
  color: ${p => p.active ? PRIMARY : '#94a3b8'};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${PRIMARY};
    color: ${PRIMARY};
    background: rgba(102, 126, 234, 0.06);
  }
`;

const ModernHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  font-family: Mulish, sans-serif;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: Mulish, sans-serif;
`;

// ─── Animations ───────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Shared layout ────────────────────────────────────────────
const BackBtn = styled.button`
  background: rgba(102, 126, 234, 0.1);
  border: none;
  border-radius: 12px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${PRIMARY};
  transition: all 0.3s ease;
  flex-shrink: 0;
  &:hover { background: rgba(102, 126, 234, 0.2); transform: translateX(-4px); }
`;

const GrupoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GrupoSelect = styled.select`
  padding: 8px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-family: Mulish, sans-serif;
  font-size: 14px;
  font-weight: 600;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  min-width: 140px;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const RefreshSelect = styled.select`
  padding: 6px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  min-width: 110px;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const TabNav = styled.div`
  display: flex;
  gap: 4px;
  background: white;
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const TabNavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: Mulish, sans-serif;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  background: ${(p) => (p.active ? PRIMARY : "transparent")};
  color: ${(p) => (p.active ? "white" : "#64748b")};
  box-shadow: ${(p) => (p.active ? `0 4px 12px rgba(102,126,234,0.35)` : "none")};
  &:hover:not([disabled]) {
    background: ${(p) => (p.active ? PRIMARY : "rgba(102,126,234,0.08)")};
    color: ${(p) => (p.active ? "white" : PRIMARY)};
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

// ─── Filter bar ───────────────────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 14px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  font-family: Mulish, sans-serif;
  white-space: nowrap;
`;

const DateInput = styled.input`
  padding: 7px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  color: #2c3e50;
  background: white;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const LoadMoreBtn = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  border: 1.5px dashed #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: ${PRIMARY}; color: ${PRIMARY}; background: #f0f4ff; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const RefreshBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: ${PRIMARY};
  color: white;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const BtnSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

// ─── Worker Config Card ───────────────────────────────────────
const CfgCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: ${fadeIn} 0.4s ease;
`;

const CfgCardHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 14px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #f0f0f0;
`;

const CfgCardTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  font-family: Mulish, sans-serif;
`;

const CfgCardHint = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-family: Mulish, sans-serif;
`;

const CfgEditBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border: 1.5px solid #667eea;
  border-radius: 8px;
  background: white;
  color: #667eea;
  font-family: Mulish, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: #f0f0ff; }
`;

const CfgGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
`;

const CfgSection = styled.div`
  padding: 16px 20px;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &:last-child { border-right: none; }
`;

const CfgSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${p => p.color || "#64748b"};
  font-family: Mulish, sans-serif;
  margin-bottom: 4px;
  padding-bottom: 6px;
  border-bottom: 2px solid ${p => p.color ? `${p.color}25` : "#f0f0f0"};
`;

const CfgItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 22px;
`;

const CfgDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$on ? p.color || "#27ae60" : "#e2e8f0"};
`;

const CfgItemLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  font-family: Mulish, sans-serif;
  flex: 1;
`;

const CfgItemVal = styled.span`
  font-size: 11px;
  font-weight: 700;
  font-family: Mulish, sans-serif;
  color: ${p => p.$on ? "#15803d" : "#94a3b8"};
`;

const CfgItemNum = styled.span`
  font-size: 12px;
  font-weight: 700;
  font-family: Mulish, sans-serif;
  color: #2c3e50;
`;

// ─── Health Panel ─────────────────────────────────────────────
const hBorder = (s) =>
  s === "ok" ? "#bbf7d0" : s === "warning" ? "#fde68a" : "#fecaca";
const hBg = (s) =>
  s === "ok" ? "#f0fdf4" : s === "warning" ? "#fffbeb" : "#fff5f5";
const hBgHeader = (s) =>
  s === "ok" ? "#dcfce7" : s === "warning" ? "#fef3c7" : "#fee2e2";
const hColor = (s) =>
  s === "ok" ? "#15803d" : s === "warning" ? "#b45309" : "#b91c1c";

const HealthCard = styled.div`
  border-radius: 16px;
  border: 1.5px solid ${p => hBorder(p.status)};
  background: ${p => hBg(p.status)};
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease;
`;

const HealthHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 20px;
  background: ${p => hBgHeader(p.status)};
  border-bottom: 1px solid ${p => hBorder(p.status)};
`;

const HealthBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  letter-spacing: 0.8px;
  white-space: nowrap;
  color: ${p => hColor(p.status)};
  border: 1.5px solid ${p => hBorder(p.status)};
  background: white;
`;

const HealthHeaderText = styled.span`
  font-size: 13px;
  font-family: Mulish, sans-serif;
  color: #374151;
  flex: 1;
`;

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  @media (max-width: 900px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 580px) { grid-template-columns: repeat(2, 1fr); }
`;

const HealthIndicator = styled.div`
  padding: 14px 18px;
  border-right: 1px solid #f0f0f0;
  background: ${p =>
    p.status === "critical" ? "#fff5f5" :
    p.status === "warning"  ? "#fffbf0" : "white"};
  &:last-child { border-right: none; }
`;

const HealthIndTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const HealthInfoIcon = styled(InfoOutlined)`
  font-size: 13px !important;
  color: #cbd5e1;
  cursor: help;
  flex-shrink: 0;
  margin-left: auto;
  transition: color 0.15s;
  &:hover { color: #667eea; }
`;

const StatusDot = styled.div`
  width: ${p => p.size === "small" ? "7px" : "9px"};
  height: ${p => p.size === "small" ? "7px" : "9px"};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p =>
    p.status === "ok"       ? "#22c55e" :
    p.status === "warning"  ? "#f59e0b" : "#ef4444"};
`;

const HealthIndLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Mulish, sans-serif;
`;

const HealthIndValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: ${p => hColor(p.status)};
  margin-bottom: 3px;
`;

const HealthIndDetail = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-family: Mulish, sans-serif;
`;

const HealthRecs = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 7px;
  background: rgba(255,255,255,0.6);
`;

const HealthRecsTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-family: Mulish, sans-serif;
  margin-bottom: 2px;
`;

const HealthRecRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  font-family: Mulish, sans-serif;
  color: ${p => p.status === "critical" ? "#b91c1c" : "#92400e"};
  line-height: 1.4;
  strong { font-weight: 700; }
`;

// ─── KPI cards ────────────────────────────────────────────────
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
`;

const KpiCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border-left: 4px solid ${(p) => p.color};
  animation: ${fadeIn} 0.4s ease;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  font-family: Mulish, sans-serif;
`;

const KpiLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #7f8c8d;
  margin-top: 4px;
  font-family: Mulish, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ─── Section card ─────────────────────────────────────────────
const SectionCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  animation: ${fadeIn} 0.4s ease;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 16px 0;
  font-family: Mulish, sans-serif;
`;
const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;
const ChartTitle = styled(SectionTitle)`
  margin: 0;
  flex: 1;
`;
const MaxChartOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  z-index: 1400;
`;
const MaxChartCard = styled.div`
  width: min(1200px, 96vw);
  max-height: 92vh;
  overflow: auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  padding: 20px;
  animation: ${fadeIn} 0.25s ease;
`;

const EmptyMsg = styled.p`
  color: #7f8c8d;
  font-family: Mulish, sans-serif;
  text-align: center;
  padding: 24px 0;
`;

// ─── Locks ────────────────────────────────────────────────────
const LockBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.active ? "#fdecea" : "#e8f5e9")};
  color: ${(p) => (p.active ? DANGER : SUCCESS)};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ActiveLockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-family: Mulish, sans-serif;
  font-size: 14px;
  &:last-child { border-bottom: none; }
`;

const LockTimelineItem = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;
`;

const LockItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  background: #fafafa;
  &:hover { background: #f0f4ff; }
`;

const LockMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  color: #4a5568;
  strong { color: #2c3e50; font-size: 14px; }
`;

const ExpandArrow = styled.span`
  transition: transform 0.2s ease;
  transform: ${(p) => (p.open ? "rotate(180deg)" : "rotate(0deg)")};
  color: #94a3b8;
  font-size: 12px;
`;

const LockDetails = styled.div`
  padding: 16px;
  background: white;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const DetailTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  font-family: Mulish, sans-serif;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  font-family: Mulish, sans-serif;
  color: #4a5568;
`;

const Tag = styled.span`
  background: #eef2ff;
  color: ${PRIMARY};
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: Mulish, sans-serif;
`;

// ─── Tables ───────────────────────────────────────────────────
const SimpleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Mulish, sans-serif;
  font-size: 13px;
`;
const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  background: #f8f9fa;
  color: #64748b;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const Tr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  &:hover { background: #fafafa; }
`;
const Td = styled.td`
  padding: 10px 12px;
  color: #2c3e50;
`;

// ─── Error chips ──────────────────────────────────────────────
const ErrorChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ErrorChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 20px;
  padding: 4px 12px;
  font-family: Mulish, sans-serif;
`;

const ErrorChipText = styled.span`
  font-size: 12px;
  color: #7f1d1d;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ErrorChipCount = styled.span`
  background: ${DANGER};
  color: white;
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
`;

// ─── Queue tab ────────────────────────────────────────────────
const QueueKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
`;

const QueueKpiCard = styled.div`
  border: 1px solid #e8e8e8;
  border-top: 3px solid ${(p) => p.color};
  border-radius: 14px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: ${fadeIn} 0.4s ease;
`;

const QueueKpiValue = styled.div`
  font-size: 38px;
  font-weight: 800;
  color: ${(p) => p.color};
  font-family: Mulish, sans-serif;
  line-height: 1;
`;

const QueueKpiLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  font-family: Mulish, sans-serif;
  margin-top: 6px;
`;

const QueueKpiSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-family: Mulish, sans-serif;
`;

const QueueTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const QueueInfoList = styled.div`
  display: flex;
  flex-direction: column;
`;

const QueueInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;

const QueueInfoKey = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  font-family: Mulish, sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
`;

const QueueInfoVal = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  font-family: Mulish, sans-serif;
  text-align: right;
`;

const QueueMonoVal = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: #64748b;
  text-align: right;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

// ─── Consolidated Tab ─────────────────────────────────────────
const ConsolidatedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConsolidatedSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0 4px;
  border-bottom: 2px solid #e2e8f0;
`;

const ConsolidatedSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: #2c3e50;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ConsolidatedCount = styled.span`
  font-size: 11px;
  font-family: Mulish, sans-serif;
  color: #94a3b8;
  font-weight: 600;
  margin-left: auto;
`;

const ConsolidatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
`;

const GSummaryCard = styled.div`
  background: white;
  border: 1.5px solid #e8e8e8;
  border-radius: 14px;
  padding: 18px 20px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
  &:hover {
    border-color: ${PRIMARY};
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
  }
`;

const GSummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const GSummaryId = styled.span`
  font-size: 15px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GStatusBadge = styled.span`
  font-size: 10px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: ${p => p.color};
  background: ${p => p.color}18;
  border: 1px solid ${p => p.color}44;
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const GSummaryKpis = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const GSummaryKpi = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GSummaryKpiVal = styled.span`
  font-size: 18px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: ${p => p.color || "#2c3e50"};
  line-height: 1;
`;

const GSummaryKpiLbl = styled.span`
  font-size: 10px;
  font-weight: 600;
  font-family: Mulish, sans-serif;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const GSummaryFooter = styled.div`
  font-size: 11px;
  font-family: Mulish, sans-serif;
  color: ${PRIMARY};
  font-weight: 700;
  text-align: right;
`;

const ConsolidatedChartRow = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 900px) { flex-direction: column; }
`;

const ConsolidatedSummaryKpis = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-left: 16px;
`;

const ConsolidatedSummaryKpi = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  span {
    font-size: 16px;
    font-weight: 800;
    font-family: Mulish, sans-serif;
    color: ${p => p.color || "#2c3e50"};
    line-height: 1;
  }
  small {
    font-size: 9px;
    font-weight: 600;
    font-family: Mulish, sans-serif;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
`;

// ─── Group detail view ─────────────────────────────────────────
const GroupDetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: white;
  border-radius: 12px;
  padding: 12px 20px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8e8e8;
  border-left: 4px solid ${PRIMARY};
  flex-wrap: wrap;
`;

const BackToConsolidatedBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-family: Mulish, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${PRIMARY};
    color: ${PRIMARY};
    background: rgba(102, 126, 234, 0.06);
  }
`;

const GroupDetailTitle = styled.span`
  font-size: 17px;
  font-weight: 800;
  font-family: Mulish, sans-serif;
  color: #2c3e50;
`;

